import React, { createContext, useCallback, useContext, useState } from 'react';
import MemberDetailModal from '../components/MemberDetailModal';

/** The member whose detail modal is open */
export interface OpenMemberRequest {
  teamId: string;
  userId: number;
}

interface MemberDetailContextValue {
  /**
   * Open the member detail modal for a team member. Safe to call from
   * anywhere a member's name is shown.
   */
  openMember: (teamId: string, userId: number) => void;
  closeMember: () => void;
}

const MemberDetailContext = createContext<MemberDetailContextValue | undefined>(
  undefined,
);

/**
 * App-wide member detail modal: any component can call `openMember` and
 * the single modal instance rendered here shows that member's profile
 * and activity. Mirrors the ChatDock "one instance, many openers" model.
 */
export const MemberDetailProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [openRequest, setOpenRequest] = useState<OpenMemberRequest | null>(
    null,
  );

  const openMember = useCallback((teamId: string, userId: number) => {
    setOpenRequest({ teamId, userId });
  }, []);

  const closeMember = useCallback(() => {
    setOpenRequest(null);
  }, []);

  return (
    <MemberDetailContext.Provider value={{ openMember, closeMember }}>
      {children}
      {openRequest && (
        <MemberDetailModal
          teamId={openRequest.teamId}
          userId={openRequest.userId}
          onClose={closeMember}
        />
      )}
    </MemberDetailContext.Provider>
  );
};

export function useMemberDetail(): MemberDetailContextValue {
  const context = useContext(MemberDetailContext);
  if (!context) {
    throw new Error(
      'useMemberDetail must be used within a MemberDetailProvider',
    );
  }
  return context;
}
